import { useEffect, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { PanelRightClose, PanelRightOpen } from "lucide-react";

interface PreviewPaneProps {
  pinned: boolean;
  onTogglePin: () => void;
  title: string;
  children: ReactNode;
}

export function PreviewPane({ pinned, onTogglePin, title, children }: PreviewPaneProps) {
  useEffect(() => {
    if (!pinned) return;
    function handler(e: KeyboardEvent) {
      if (e.key === "Escape") onTogglePin();
    }
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [pinned, onTogglePin]);

  if (!pinned) return null;

  return (
    <div
      className="w-[360px] flex-shrink-0 border-l border-border bg-background overflow-hidden"
    >
      <div className="flex h-full w-[360px] flex-col">
        <div className="border-b border-border px-4 py-3">
          <h2 className="text-base font-semibold">{title}</h2>
        </div>
        <div className="flex flex-1 flex-col gap-3 overflow-y-auto p-4">
          {children || (
            <p className="text-sm text-muted-foreground">Click a row to preview details</p>
          )}
        </div>
      </div>
    </div>
  );
}

/** Toggle button for the preview pane -- place in page header */
export function PreviewPaneToggle({ pinned, onTogglePin }: { pinned: boolean; onTogglePin: () => void }) {
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onTogglePin}
      title={pinned ? "Close preview panel" : "Open preview panel"}
    >
      {pinned ? <PanelRightClose className="h-4 w-4" /> : <PanelRightOpen className="h-4 w-4" />}
    </Button>
  );
}

export function PreviewField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <span className="text-sm">{children}</span>
    </div>
  );
}
